import React, { useEffect, ReactNode } from "react";
import { ThemeProvider } from "@material-ui/core";
import { Redirect, Route, Switch } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, RouteProps } from "react-router";

import { history } from "redux/store";
import { MAIN_ROUTES, AppRoute } from "MainRoutes";
import { UI_THEME } from "utils/theme";
import { AppWrapper } from "components/layout/Wrapper";
import { fetchLogRequestUserMappings } from "redux/reducers/logRequestReducer";
import {
    checkAuthStatusAction,
    loadUserState,
} from "redux/reducers/userReducer";
import { updateLogger } from "utils/logger";
import { RootState } from "redux/reducers/rootReducer";
import { useRecursiveTimeout } from "hooks/useRecursiveTimeout";

interface PrivateRouteProps extends RouteProps {
    protectedRoute: boolean;
    isLoggedIn: boolean;
    children: ReactNode;
}

function PrivateRoute({
    protectedRoute,
    isLoggedIn,
    path,
    children,
    ...rest
}: PrivateRouteProps) {
    const routeRender = ({ location }: RouteComponentProps<any>) => {
        const redirectParams = {
            pathname: "/login",
            state: { from: location },
        };
        const redirect = <Redirect to={redirectParams} />;
        if (protectedRoute && !isLoggedIn) {
            return redirect;
        }
        return children;
    };
    return <Route {...rest} render={routeRender} />;
}

export default function App() {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const didAttemptPreviousStateLoad = useSelector(
        (state: RootState) => state.user.didAttemptPreviousStateLoad
    );

    const generateRoute = (routeParams: AppRoute) => {
        // Don't force a redirect until we have tried to load the user state
        if (!didAttemptPreviousStateLoad) {
            return null;
        }
        const WrappedComponent = routeParams.disableAppWrapper ? (
            routeParams.component
        ) : (
            <AppWrapper>{routeParams.component}</AppWrapper>
        );

        return (
            <PrivateRoute
                key={routeParams.path}
                path={routeParams.path}
                isLoggedIn={isLoggedIn}
                protectedRoute={routeParams.protected}
            >
                {WrappedComponent}
            </PrivateRoute>
        );
    };

    const checkAuthStatus = (): Promise<any> => {
        if (isLoggedIn) {
            return Promise.resolve(
                dispatch({ type: checkAuthStatusAction.toString() })
            );
        }
        return Promise.resolve(true);
    };

    const getLatestData = (): Promise<any> => {
        if (didAttemptPreviousStateLoad && isLoggedIn) {
            return Promise.resolve(dispatch(fetchLogRequestUserMappings()));
        }
        return Promise.resolve(true);
    };

    useRecursiveTimeout(checkAuthStatus, 30 * 1000);
    useRecursiveTimeout(getLatestData, 10 * 1000);

    useEffect(() => {
        dispatch({ type: loadUserState.toString() });
        dispatch({ type: checkAuthStatusAction.toString() });
        updateLogger();
    }, [dispatch]);

    useEffect(() => {
        if (didAttemptPreviousStateLoad && isLoggedIn) {
            dispatch(fetchLogRequestUserMappings());
        }
    }, [dispatch, isLoggedIn, didAttemptPreviousStateLoad]);

    return (
        <ThemeProvider theme={UI_THEME}>
            <ConnectedRouter history={history}>
                <Switch>
                    {didAttemptPreviousStateLoad && (
                        <Route exact={true} path="/">
                            <Redirect to="/log" />
                        </Route>
                    )}
                    {Object.values(MAIN_ROUTES).map((route) =>
                        generateRoute(route)
                    )}
                </Switch>
            </ConnectedRouter>
        </ThemeProvider>
    );
}
